import Vue from 'vue'
import gql from 'graphql-tag'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Account } from '@/modules/Account/interfaces/account'
import { showErrors } from '@/utils/snack'
import { dateTimeToDisplay } from '@/utils/dateTime'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import { formatPhoneNumber } from '@/utils/phoneNumberFormat'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import ContactDialog from '@/modules/Contact/scripts/contact-dialog'
import AccountDialog from '@/modules/Account/scripts/account-dialog'
import RelationFormDialog from '@/modules/AccountContactRelation/components/RelationFormDialog.vue'
import RelationDialog from '@/modules/AccountContactRelation/scripts/account-contact-relation-dialog'
import { getAccounts, getAllGlobalSearchAccounts } from '@/modules/Account/gql/account.gql'
import ListPage from '@/mixins/list-page'
import SearchProducer from '@/mixins/search-queries/search-producer'
import SearchUser from '@/mixins/search-queries/search-user'
import SearchBook from '@/mixins/search-queries/search-book'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppSimpleTextSearch from '@/components/AppSimpleTextSearch.vue'
import { PageState } from '../../../store/modules/page'
import { accountLevel, displayAccountLevel } from '../helper/account-level'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField,
    RelationFormDialog,
    AppSelector,
    AppListSearchBtn,
    AppTextSearch,
    AppFilterButton,
    AppSimpleTextField,
    AppSimpleTextSearch
  }
})
export default class AccountsList extends mixins(AccountDialog, ContactDialog, RelationDialog, ListPage, mixins(SearchProducer, SearchUser, SearchBook)) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Client Information'
    },
    {
      text: 'Accounts'
    }
  ]
  searchVal = ''

  accountLevel = accountLevel
  displayAccountLevel = displayAccountLevel
  withOpenCase = false

  accounts: Account[] = []
  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    name: 'NAME',
    phone: 'PHONE',
    caseCount: 'CASE_COUNT',
    updatedAt: 'UPDATED_AT'
  }

  defaultSort = 'UPDATED_AT'

  dateTimeToDisplay = dateTimeToDisplay

  pageType = ''

  tableColumn = [
    { field: 'name', title: 'Account Name', slots: { default: 'name_default' }, sortable: true, resizable: true },
    { field: 'level', title: 'Level', slots: { default: 'level_default' }, sortable: false, with: 60, resizable: true },
    {
      title: 'Phone',
      field: 'phone',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.phone ? formatPhoneNumber(row.phone, row.countryCode) : '-')
          ]
        }
      },
      resizable: true,
      sortable: true
    },
    {
      title: 'Ext',
      field: 'extensionNumber',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.extensionNumber ? row.extensionNumber : '-')
          ]
        }
      },
      width: 80,
      resizable: true,
      sortable: false
    },
    {
      title: 'Book',
      field: 'book',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.book ? row.book.name : '-')
          ]
        }
      },
      resizable: true,
      sortable: false
    },
    {
      title: 'Source',
      field: 'source',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.source ? row.source.name : '-')
          ]
        }
      },
      resizable: true,
      sortable: false
    },
    {
      title: 'CSR',
      field: 'csr',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.csr ? row.csr.username : '-')
          ]
        }
      },
      resizable: true,
      sortable: false
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 74 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'id',
    sortDesc: true
  }

  tableLoading = false

  @State(state => state.page) page!: PageState

  @Watch('$route')
  onRouteChanged (val: any) {
    const { type } = this.$route.params
    if (type) {
      this.pageType = this.$route.params.type
    } else {
      this.pageType = ''
    }
    this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  get isShowFilter () {
    return this.$route.name !== 'AccountsListWithSearchval'
  }

  async created () {
    const { search, type } = this.$route.params
    this.searchVal = search
    this.restorePage(this.page[this.$route.name || ''])
  }

  async mounted () {
    this.onResize()
    this.pageType = this.$route.params.type
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (item: any, subPage = 'Detail') {
    return {
      name: `AccountDetail${subPage}`,
      params: {
        id: item.id
      }
    }
  }

  async getAccounts () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || this.defaultSort
    const filter: {
      [key: string]: any;
    } = {}
    if (this.filter.accountName) {
      filter.name = this.filter.accountName
    }
    if (this.filter.phone) {
      filter.phone = this.filter.phone
    }
    if (this.filter.contactId) {
      filter.contactId = +this.filter.contactId
    }
    if (this.filter.isRelated) {
      filter.isRelated = this.filter.isRelated === '2'
    }
    if (this.filter.book && this.filter.book.id) {
      filter.bookId = Number(this.filter.book.id)
    }
    if (this.filter.csr && this.filter.csr.id) {
      filter.csrId = Number(this.filter.csr.id)
    }
    if (this.filter.source && this.filter.source.id) {
      filter.sourceId = Number(this.filter.source.id)
    }

    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAccounts,
        variables: {
          filter,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.accounts = result.data.getAccounts.accounts
      this.tableLoading = false
      this.options.tablePage.total = result.data.getAccounts.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getAccountsFromDashboard () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || this.defaultSort
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAllGlobalSearchAccounts,
        variables: {
          query: this.$route.params.search,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.accounts = result.data.globalSearchAccounts.accounts
      this.tableLoading = false
      this.options.tablePage.total = result.data.globalSearchAccounts.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async deleteAcctConfirm () {
    await this.deleteAcctPost()
    this.getList()
  }

  onCreateSuccess () {
    this.hideAcctEditDialog()
    this.getList()
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  routeToRelationship (e: Event, item: any) {
    e.preventDefault()
    e.stopPropagation()
    this.routeTo('AccountContactRelationDetail', {
      id: item.relation.id
    })
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getList()
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getList()
  }

  getList () {
    if (this.$route.name === 'AccountsListWithSearchval') {
      this.getAccountsFromDashboard()
    } else {
      if (this.pageType !== '') {
        this.filter.contactId = Number(this.$route.params.id)
        if (this.pageType === 'account') {
          this.filter.isRelated = '1'
        } else {
          this.filter.isRelated = '2'
        }
      } else {
        this.filter.contactId = undefined
        this.filter.isRelated = undefined
      }
      this.getAccounts()
    }
  }
}
